/**
 * The terms and privacy documents, and the languages they exist in.
 *
 * Shared rather than owned by the gate modal: the Privacy tab in user settings links the same two
 * documents, and a URL that lives in two places is a URL that will be updated in one of them.
 */

/**
 * The languages the terms are offered in, in the order the gate's switch shows them. Mirrors
 * `TermsProvider.OfferedLanguages` on the backend; the switch is built by intersecting this list
 * with what the response actually carries, so a language missing from either side simply isn't
 * offered rather than producing a button with nothing behind it.
 *
 * Note this is NOT the application's language list. The terms do not follow the UI language:
 * each version is independently binding, so the reader picks which one they are accepting.
 */
export const TERMS_LANGUAGES = ['de', 'en'] as const;

export type TermsLanguage = typeof TERMS_LANGUAGES[number];

/**
 * The published terms PDFs, so a reader can keep a copy of what they agreed to rather than only
 * seeing it inside a modal they are about to dismiss.
 */
export const TERMS_PDF_URL: Record<TermsLanguage, string> = {
    de: 'https://www.bimagine.com/Files/231121_BIX_LEG_DOC_001_General-Terms-BIMAGINE_DE.pdf',
    en: 'https://www.bimagine.com/Files/231121_BIX_LEG_DOC_001_General-Terms-BIMAGINE_ENG.pdf',
};

/**
 * The privacy policy the terms incorporate by reference — see their PRIVACY POLICY /
 * DATENSCHUTZRICHTLINIE section.
 */
export const PRIVACY_POLICY_URL: Record<TermsLanguage, string> = {
    de: 'https://www.bimagine.com/Files/260910_BTO_MAR_DOC_001_Privacy_Policy_ZEITON_DE.pdf',
    en: 'https://www.bimagine.com/Files/260910_BTO_MAR_DOC_001_Privacy_Policy_ZEITON_ENG.pdf',
};

/**
 * Which document language to offer someone who has not picked one — inside the gate modal that is
 * the switch's starting position, and outside it (user settings) it is the only rule there is,
 * because there is nothing to pick with.
 *
 * The documents exist in German and English only, so the application's other three languages land
 * on English. That is a fallback, not a statement about which version governs: both bind
 * independently, and the shipped terms carry no governing-language clause.
 */
export function defaultDocumentLanguage(appLanguage: string | undefined | null): TermsLanguage {
    return appLanguage?.toLowerCase().startsWith('de') ? 'de' : 'en';
}
